import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Testimonials: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: 'Silvia H. Barreiro',
      role: 'Gestora das Academias G.B.',
      quote: 'A Raquel de Araujo Roble transformou a identidade visual das academias Gustavo Borges. Seu olhar atento e criativo trouxe uma nova vida aos nossos espaços, refletindo não apenas a filosofia da nossa marca, mas também as necessidades dos nossos clientes. A colaboração foi fluida e ela sempre se mostrou acessível e aberta a feedbacks. Estamos extremamente satisfeitos com os resultados e recomendo seus serviços de design a qualquer pessoa ou empresa que busque inovação e profissionalismo.'
    },
    {
      id: 2,
      name: 'Cristiane Aparecida Derinievicz Holthmam',
      role: 'Ball Pen Papelaria e Informatica',
      quote: 'Queria agradecer ao trabalho da Raquel na minha loja, tanto a reforma da fachada bem como a modernização da loja por dentro, fiquei muito satisfeita com o resultado, a loja por dentro ficou muito clara e muito bem setorizada, sem contar que reaproveitamos todos os móveis que já existiam, isso me deixou muito satisfeita, porque adoro poder reaproveitar móveis, sem contar o bem que fizemos ao bolso e ao meio ambiente, da fachada recebo muitos elogios com sua criatividade, e a comunicação visual da fachada ficou top mesmo. Gratidão por tudo, sempre que me perguntam quem fez o projeto tenho muito carinho em dar seu contato.'
    },
    {
      id: 3,
      name: 'Nair Gregolin',
      role: 'Gregolin Mercado',
      quote: 'A Raquel fez um excelente trabalho na minha loja. Durante a reforma, ela foi a DESIGNER E ENGENHEIRA responsável por todo o projeto e me deu total respaldo. O layout que ela criou ficou maravilhoso, muito bem planejado e executado, trazendo funcionalidade e beleza ao espaço. Seu profissionalismo e dedicação fizeram toda a diferença no resultado final.'
    },
    {
      id: 4,
      name: 'Angela Peters',
      role: 'Triunfo Supermercado',
      quote: 'Raquel é uma profissional criativa, competente e atenciosa. Fiz vários trabalhos com ela e sempre honrou os prazos. Indico o trabalho dela de olhos fechados!'
    },
    {
      id: 5,
      name: 'Iraci Negherbon',
      role: 'Elemento Feminino, Loja de Confecção',
      quote: 'Somos de Santa Catarina, quatro lojas de moda de diferentes perfis, já fizemos a muito tempo vários trabalhos de consultoria de design com a Raquel, que sempre entendeu fácil nossas necessidades, com respostas e projetos muito assertivos. Atualmente desenvolvendo mais um projeto.'
    }
  ];

  const nextTestimonial = useCallback(() => {
    setDirection(1);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  }, [testimonials.length]);

  const prevTestimonial = useCallback(() => {
    setDirection(-1);
    setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      nextTestimonial();
    }, 8000);
    return () => clearInterval(interval);
  }, [nextTestimonial]);

  const slideVariants = {
    enter: (direction: number) => ({
        x: direction > 0 ? 1000 : -1000,
        opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
        x: direction < 0 ? 1000 : -1000,
        opacity: 0
    })
  };

  const testimonial = testimonials[currentIndex];

  return (
    <section className="section bg-neutral-light py-20 overflow-hidden">
      <div className="container">
        <div className="text-center mb-12">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-bold text-neutral-dark mb-4"
          >
            Depoimentos
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-neutral-dark/70 max-w-2xl mx-auto"
          >
            O que nossos clientes dizem sobre nossos serviços
          </motion.p>
        </div>

        <div className="relative max-w-4xl mx-auto">
          <div className="relative" style={{ minHeight: '300px' }}>
            <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={testimonial.id}
              custom={direction}
                variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
                className="bg-white p-8 md:p-12 rounded-lg shadow-md"
            >
              <div className="absolute top-10 left-10 text-primary opacity-20 text-5xl">
                "
                </div>
                <div className="relative z-10 px-4">
                  <p className="text-lg md:text-xl text-neutral-dark/80 italic mb-6">"{testimonial.quote}"</p>
                  <div className="flex flex-col items-center md:items-start border-t border-primary/10 pt-4">
                    <h4 className="text-lg font-bold text-neutral-dark">{testimonial.name}</h4>
                    <p className="text-primary">{testimonial.role}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
          </div>

          <div className="flex justify-center mt-8 gap-4">
            <button 
              onClick={prevTestimonial}
              className="w-10 h-10 rounded-full bg-white text-primary border border-primary/20 flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
            >
              &larr;
            </button>
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button 
                  key={index}
                  className={`w-3 h-3 rounded-full ${currentIndex === index ? 'bg-primary' : 'bg-primary/30'}`}
                  onClick={() => {
                    setDirection(index > currentIndex ? 1 : -1);
                    setCurrentIndex(index);
                  }}
                />
              ))}
            </div>
            <button 
              onClick={nextTestimonial}
              className="w-10 h-10 rounded-full bg-white text-primary border border-primary/20 flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
            >
              &rarr;
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials; 